import React, { useEffect, useState } from "react";
import { useLanguagesContext } from "../../contexts/LanguagesContext";
import Select from 'react-select';
import Button from "../general/Button";
import useDecks from "../../hooks/useDecks";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faTrash, faX, faVolumeUp, faDownload, faCopy } from "@fortawesome/pro-solid-svg-icons";
import useAI from "../../hooks/useAI";
import ProofreaderModal from "./modals/ProofreaderModal";
import AudioRegenerationModal from "./modals/AudioRegenerationModal";
import Modal from "../general/Modal";
import NotificationMessage from "../general/NotificationMessage";

const googleTtsLanguages = [
  "af", // Afrikaans
  "ar", // Arabic
  "bg", // Bulgarian
  "bn", // Bengali
  "bs", // Bosnian
  "ca", // Catalan
  "cs", // Czech
  "cy", // Welsh
  "da", // Danish
  "de", // German
  "el", // Greek
  "en", // English
  "es", // Spanish
  "et", // Estonian
  "eu", // Basque
  "fi", // Finnish
  "tl", // tagalog
  "fr", // French
  "gl", // Galician
  "gu", // Gujarati
  "he", // Hebrew
  "hi", // Hindi
  "hr", // Croatian
  "hu", // Hungarian
  "hy", // Armenian
  "id", // Indonesian
  "is", // Icelandic
  "it", // Italian
  "ja", // Japanese
  "jv", // Javanese
  "ka", // Georgian
  "km", // Khmer
  "kn", // Kannada
  "ko", // Korean
  "lt", // Lithuanian
  "lv", // Latvian
  "ml", // Malayalam
  "mr", // Marathi
  "ms", // Malay
  "mt", // Maltese
  "my", // Burmese
  "no", // Norwegian Bokmål
  "ne", // Nepali
  "nl", // Dutch
  "pa", // Punjabi
  "pl", // Polish
  "pt", // Portuguese
  "ro", // Romanian
  "ru", // Russian
  "si", // Sinhala
  "sk", // Slovak
  "sl", // Slovenian
  "sq", // Albanian
  "sr", // Serbian
  "su", // Sundanese
  "sv", // Swedish
  "sw", // Swahili
  "ta", // Tamil
  "te", // Telugu
  "th", // Thai
  "tr", // Turkish
  "uk", // Ukrainian
  "ur", // Urdu
  "vi", // Vietnamese
  "zh", // Chinese (Mandarin and Cantonese)
];

const MultiLingualDeckTable = ({deck, dbCards}) => {
  const [columns, setColumns] = useState(["en"]);
  const [rows, setRows] = useState([{}]);
  const [saving, setSaving] = useState(false);
  const [aiWorking, setAiWorking] = useState(false);
  const [fillInMissingWorking, setFillInMissingWorking] = useState(false);
  const [fillInMissingProgress, setFillInMissingProgress] = useState({ current: 0, total: 0 });
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [showAIPrompt, setShowAIPrompt] = useState(false);
  const [showProofreaderModal, setShowProofreaderModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showAudioRegenerationModal, setShowAudioRegenerationModal] = useState(false);
  const [exportedTableText, setExportedTableText] = useState("");
  const [showNotification, setShowNotification] = useState(false);
  const [notificationType, setNotificationType] = useState("success");
  const [notificationMessage, setNotificationMessage] = useState("");
  const {saveChangesToDeck, 
    addItemToDeck, 
    updateMultilingualItemInDeck, 
    deleteDeckCard
  } = useDecks();

  const {generateAICardsForDeck, generateMultilingualDeckAudio} = useAI();

  useEffect(()=> {
    if (deck){
        if (deck.hasOwnProperty("languages")){
            setColumns(deck.languages);
        }
    };
  },[deck]);

  useEffect(() => {
    if (dbCards){
        let rows_ = [];
        dbCards.forEach(card=>{
            console.log("card: ", card);
            let row = {card_id: card["id"]};
            deck.languages.forEach((lang, index)=>{
                row[lang] = card["terms"].hasOwnProperty(lang) ? card["terms"][lang] : null;
            }); 
            // Store audio data reference for this row
            row._audioData = card["multilingual_google_tts"] || {};
            rows_.push(row);
        });
        setRows(rows_);
    }
    
  }, [dbCards]);

  // Helper function to check if a row has audio for a specific language
  const hasAudioForLanguage = (row, language) => {
    if (!row._audioData || !language) return false;
    const audioData = row._audioData;
    const audioUrl = audioData[language];
    // Check if this specific language has a valid audio URL
    return audioUrl && typeof audioUrl === 'string' && audioUrl.startsWith('https://');
  };

  // Helper function to get audio URL for a specific language
  const getAudioUrlForLanguage = (row, language) => {
    if (!row._audioData || !language) return null;
    const audioData = row._audioData;
    return audioData[language] || null;
  };

  // Function to play audio
  const playAudio = (audioUrl) => {
    if (!audioUrl) return;
    try {
      const audio = new Audio(audioUrl);
      audio.play().catch(error => {
        console.error('Error playing audio:', error);
      });
    } catch (error) {
      console.error('Error creating audio element:', error);
    }
  };

  const {allLanguages, sourceLanguageOptions} = useLanguagesContext();

  const addColumn = () => {
    setColumns([...columns, null]);
  };

  const addRow = () => {
    setRows([...rows, {}]);
  };

  const handleInputChange = (rowIndex, language, value) => {
    const updatedRows = [...rows];
    updatedRows[rowIndex][language] = value;
    setRows(updatedRows);
  };

  const languageOnChange = (option, colIndex) => {
    const updatedColumns = [...columns];
    updatedColumns[colIndex] = option.value;
    setColumns(updatedColumns);
  };

  const saveCardsToDatabase = async () => {
    let updatedRows = [...rows]; // Work with a copy to avoid race conditions

    for (let i = 0; i < updatedRows.length; i++) {
        let row = updatedRows[i];

        let terms = {};
        let hasAValue = false;

        columns.forEach((lang) => {
            if (row[lang]) hasAValue = true;
            terms[lang] = row[lang] || null;
        });

        if (!hasAValue) continue; // Skip empty rows

        if (!row.card_id) {
            // Add new card
            let res = await addItemToDeck({ deck_id: deck.id, multilingual: true, multilingualTerms: terms });

            if (res?.id) {
                updatedRows[i] = { ...row, card_id: res.id };
            }
        } else {
            // Update existing card
            await updateMultilingualItemInDeck({ cardDocId: row.card_id, multilingualTerms: terms });
        }
    }

    setRows(updatedRows); // Only update state once at the end
};


  const saveChangesClick = async () => {
    console.log("Columns: ", columns);
    console.log("Rows: ", rows);
    setSaving(true);
    //update languages in deck
    await saveChangesToDeck({languages: columns.filter((col)=>col !== null), deckId: deck.id});
    // add or update the cards
    await saveCardsToDatabase();
    setSaving(false);
  };

  const toggleAIAssistant = () => {
    setShowAIPrompt(!showAIPrompt);
  }

  // Helper function to truncate rows data for AI requests
  const truncateRowsForAI = (rowsData, maxRows = 50) => {
    if (!rowsData || rowsData.length === 0) return [];
    
    // Filter out empty rows and limit to maxRows
    const filteredRows = rowsData
      .filter(row => {
        // Keep rows that have at least one non-empty value
        return Object.values(row).some(val => val && val !== null && val !== "");
      })
      .slice(0, maxRows);
    
    return filteredRows;
  }

  // Helper function to format language list for prompt
  // Includes all languages but in a compact format
  const formatLanguagesForPrompt = (languages) => {
    const validLanguages = languages.filter(lang => lang !== null);
    // Always include all languages so AI knows what to generate
    return validLanguages.join(", ");
  }

  const generateAICardsClick = async ({prompt=null}) => {
    setAiWorking(true);
    
    // Truncate rows to prevent oversized prompts
    const truncatedRows = truncateRowsForAI(rows, 50);
    const formattedLanguages = formatLanguagesForPrompt(columns);
    
    console.log("Rows (truncated) and prompt: ", truncatedRows.length, "rows sent (out of", rows.length, "total)", prompt);
    
    let results = await generateAICardsForDeck({
        prompt: (!prompt ? `Make cards that correspond to this topic provided by the user: ${aiPrompt}.
        The user asked for the languages ${formattedLanguages}. If you fill out existing values, make sure that you keep the original keys as well for the other languages along with the data. Return the full json object.
        (but if some languages are missing translations you should add them and preserve the existing items) Here is the json the user submitted (showing ${truncatedRows.length} of ${rows.length} rows): ${JSON.stringify(truncatedRows)}`
        :
        prompt)
    });


    console.log("Results: ", results);
    if (Array.isArray(results) && results.length > 0) {
        let newRows = [...rows];

        results.forEach((card) => {
            let row = {};
            columns.forEach((lang) => {
                // Only assign if the value exists and is not empty/null
                const value = card.hasOwnProperty(lang) ? card[lang] : null;
                row[lang] = (value !== null && value !== undefined && value !== "") ? value : null;
            });

            // Find existing row by matching any non-empty value (prefer first column, but fallback to any matching value)
            let existingRowIndex = -1;
            const firstCol = columns[0];
            
            // First try to match by first column if both have values
            if (row[firstCol] && row[firstCol] !== null && row[firstCol] !== "") {
                existingRowIndex = newRows.findIndex((r) => {
                    return r[firstCol] && r[firstCol] === row[firstCol];
                });
            }
            
            // If no match found, try matching by any common non-empty value
            if (existingRowIndex === -1) {
                existingRowIndex = newRows.findIndex((r) => {
                    return columns.some(lang => {
                        const rVal = r[lang];
                        const rowVal = row[lang];
                        return rVal && rowVal && rVal !== "" && rowVal !== "" && rVal === rowVal;
                    });
                });
            }

            if (existingRowIndex !== -1) {
                // Merge translations for existing row - only add non-empty values to fill missing translations
                let mergedRow = { ...newRows[existingRowIndex] };
                columns.forEach((lang) => {
                    // Only merge if the existing value is missing (null/empty) and the new value is valid
                    const existingValue = mergedRow[lang];
                    const newValue = row[lang];
                    const isExistingMissing = !existingValue || existingValue === null || existingValue === "";
                    const isNewValueValid = newValue !== null && newValue !== undefined && newValue !== "";
                    
                    if (isExistingMissing && isNewValueValid) {
                        mergedRow[lang] = newValue;
                    }
                });
                newRows[existingRowIndex] = mergedRow;
            } else {
                // Only add row if it has at least one valid translation
                const hasAtLeastOneValue = columns.some(lang => row[lang] !== null && row[lang] !== undefined && row[lang] !== "");
                if (hasAtLeastOneValue) {
                    newRows.push(row);
                }
            }
        });
        //capitliaze first letter
        newRows.forEach((row)=>{
            columns.forEach((lang)=>{
                if (row.hasOwnProperty(lang) && row[lang] !== null && row[lang] !== undefined && typeof row[lang] === 'string' && row[lang].length > 0){
                    row[lang] = row[lang].charAt(0).toUpperCase() + row[lang].slice(1);
                }
            });
        });
        setRows(newRows);
    } else if (results === null || (Array.isArray(results) && results.length === 0)) {
        console.warn("AI returned empty results. This might mean:");
        console.warn("1. The prompt was too complex or unclear");
        console.warn("2. The AI couldn't generate translations");
        console.warn("3. There was an error processing the request");
        alert("AI did not return any translations. Please try again or check the console for details.");
    }

    setAiWorking(false);
};


  const deleteRow = async (row, rowIndex) => {
    let newRows = [...rows];
    newRows.splice(rowIndex, 1);
    setRows(newRows);
    console.log("deleting row: ", row);
    if (row.hasOwnProperty("card_id")){
        //delete card from database
        await deleteDeckCard({cardDocId: row.card_id});
    }
  };

  const addDefaultLanguages = () => {
    const list = ["en", "fr", "no", "es", "zh", "de", "it", "nl", "da", "sv", "pt", "fi", "ro", "is", "ru"];
    // add if not already present
    const newColumns = [...columns];
    list.forEach((language) => {
        if (!newColumns.includes(language)){
            newColumns.push(language);
        }
    });
    setColumns(newColumns);
  }

  const addAllTtsLanguages = () => {
    // add if not already present
    const newColumns = [...columns];
    googleTtsLanguages.forEach((language) => {
        if (!newColumns.includes(language)){
            newColumns.push(language);
        }
    });
    setColumns(newColumns);
  }

  const deleteLanguage = async (languageToDelete, colIndex) => {
    // Prevent deletion if it's the last language
    const validColumns = columns.filter((col) => col !== null);
    if (validColumns.length <= 1) {
        alert("Cannot delete the last language. A deck must have at least one language.");
        return;
    }

    setSaving(true);

    // Remove from columns
    const newColumns = [...columns];
    newColumns.splice(colIndex, 1);
    setColumns(newColumns);

    // Remove from all rows
    const newRows = rows.map((row) => {
        const updatedRow = { ...row };
        delete updatedRow[languageToDelete];
        return updatedRow;
    });
    setRows(newRows);

    // Update all cards in database to remove this language from terms
    for (const row of newRows) {
        if (row.card_id) {
            // Build terms object without the deleted language
            const terms = {};
            newColumns.forEach((lang) => {
                if (lang !== null && row.hasOwnProperty(lang)) {
                    terms[lang] = row[lang] || null;
                }
            });
            
            // Update the card in database
            await updateMultilingualItemInDeck({ 
                cardDocId: row.card_id, 
                multilingualTerms: terms 
            });
        }
    }

    // Update deck languages in database
    await saveChangesToDeck({
        languages: newColumns.filter((col) => col !== null), 
        deckId: deck.id
    });

    setSaving(false);
  }

  const capitalizeClick = () => {
    let newRows = [...rows];
    newRows.forEach((row)=>{
        columns.forEach((lang)=>{
            if (row.hasOwnProperty(lang) && row[lang] !== null && row[lang] !== undefined && typeof row[lang] === 'string' && row[lang].length > 0){
                row[lang] = row[lang].charAt(0).toUpperCase() + row[lang].slice(1);
            }
        });
    });
    setRows(newRows);
  }

  const deleteDuplicateRows = async () => {
    let seen = new Set();
    let newRows = [];
    let deletedRows = [];

    for (const row of rows) {
        let firstCol = row[columns[0]];
        if (!seen.has(firstCol)) {
            seen.add(firstCol);
            newRows.push(row);
        } else {
            deletedRows.push(row);
        }
    }

    // Delete rows from the database
    for (const row of deletedRows) {
        if (row.hasOwnProperty("card_id")) {
            console.log("Deleting card: ", row);
            await deleteDeckCard({ cardDocId: row.card_id });
        }
    }

    // Update the state AFTER deletion
    setRows(newRows);
};

  // Helper function to merge AI results into existing rows
  const mergeAITranslationsIntoRows = (aiResults, currentRows) => {
    let updatedRows = [...currentRows];
    const validColumns = columns.filter(col => col !== null);
    
    aiResults.forEach((card) => {
      let row = {};
      validColumns.forEach((lang) => {
        const value = card.hasOwnProperty(lang) ? card[lang] : null;
        row[lang] = (value !== null && value !== undefined && value !== "") ? value : null;
      });

      // Find existing row by matching first column value
      const firstCol = columns[0];
      let existingRowIndex = -1;
      
      if (row[firstCol] && row[firstCol] !== null && row[firstCol] !== "") {
        existingRowIndex = updatedRows.findIndex((r) => {
          return r[firstCol] && r[firstCol] === row[firstCol];
        });
      }
      
      // If no match, try matching by any common non-empty value
      if (existingRowIndex === -1) {
        existingRowIndex = updatedRows.findIndex((r) => {
          return validColumns.some(lang => {
            const rVal = r[lang];
            const rowVal = row[lang];
            return rVal && rowVal && rVal !== "" && rVal === rowVal;
          });
        });
      }

      if (existingRowIndex !== -1) {
        // Merge translations for existing row
        let mergedRow = { ...updatedRows[existingRowIndex] };
        validColumns.forEach((lang) => {
          const existingValue = mergedRow[lang];
          const newValue = row[lang];
          const isExistingMissing = !existingValue || existingValue === null || existingValue === "";
          const isNewValueValid = newValue !== null && newValue !== undefined && newValue !== "";
          
          if (isExistingMissing && isNewValueValid) {
            mergedRow[lang] = newValue;
          }
        });
        updatedRows[existingRowIndex] = mergedRow;
      }
    });
    
    return updatedRows;
  };

  const fillInMissingTranslationsWithAI = async () => {
    // Only include rows that have missing translations
    const validColumns = columns.filter(col => col !== null);
    const rowsWithMissingTranslations = rows.filter(row => {
      const hasAtLeastOneValue = validColumns.some(lang => row[lang] && row[lang] !== null && row[lang] !== "");
      const hasMissingTranslations = validColumns.some(lang => !row[lang] || row[lang] === null || row[lang] === "");
      return hasAtLeastOneValue && hasMissingTranslations;
    });
    
    if (rowsWithMissingTranslations.length === 0) {
      alert("No rows with missing translations found.");
      return;
    }
    
    const formattedLanguages = formatLanguagesForPrompt(columns);
    // Process 1 card at a time to handle many languages (75+) - each card can be large
    const BATCH_SIZE = 1;
    let processedCount = 0;
    let currentRows = [...rows];
    
    setFillInMissingWorking(true);
    setFillInMissingProgress({ current: 0, total: rowsWithMissingTranslations.length });
    
    try {
      // Process in batches
      for (let i = 0; i < rowsWithMissingTranslations.length; i += BATCH_SIZE) {
        const batch = rowsWithMissingTranslations.slice(i, i + BATCH_SIZE);
        console.log(`Processing batch ${Math.floor(i / BATCH_SIZE) + 1}: ${batch.length} cards`);
        
        // Format cards for AI
        const cardsForAI = batch.map(row => {
          const card = {};
          validColumns.forEach(lang => {
            const value = row[lang];
            card[lang] = (value && value !== null && value !== "") ? value : null;
          });
          return card;
        });

        const batchNumber = Math.floor(i / BATCH_SIZE) + 1;
        console.log(`Batch ${batchNumber} - Cards for AI:`, JSON.stringify(cardsForAI, null, 2));
        console.log(`Batch ${batchNumber} - Number of languages: ${validColumns.length}`);
        console.log(`Batch ${batchNumber} - Languages: ${formattedLanguages}`);

        const prompt = `TRANSLATION TASK: Fill in missing translations for existing cards.

CRITICAL INSTRUCTIONS:
- You MUST return a JSON array with EXACTLY ${batch.length} card objects (same number as input)
- DO NOT return an empty array
- For each card: keep existing translations EXACTLY as they are, translate null values
- Return ALL ${validColumns.length} languages for each card: ${formattedLanguages}

Example:
Input: [{"en": "Hello", "fr": null, "es": null}]
Output: [{"en": "Hello", "fr": "Bonjour", "es": "Hola"}]

Cards to translate (${batch.length} cards):
${JSON.stringify(cardsForAI)}`;

        console.log(`Batch ${batchNumber} - Prompt length: ${prompt.length} characters`);
        console.log(`Batch ${batchNumber} - Calling AI...`);

        // Call AI for this batch with retry logic
        let results = null;
        let retryCount = 0;
        const MAX_RETRIES = 2;
        
        while (retryCount <= MAX_RETRIES) {
          try {
            results = await generateAICardsForDeck({ prompt });
            
            console.log(`Batch ${batchNumber} AI results (attempt ${retryCount + 1}):`, results);
            console.log(`Batch ${batchNumber} - Results type: ${typeof results}, isArray: ${Array.isArray(results)}, length: ${Array.isArray(results) ? results.length : 'N/A'}`);
            
            if (Array.isArray(results) && results.length > 0) {
              console.log(`Batch ${batchNumber} - First result:`, JSON.stringify(results[0], null, 2));
              break; // Success, exit retry loop
            } else if (results && typeof results === 'object') {
              console.log(`Batch ${batchNumber} - Results object keys:`, Object.keys(results));
              console.log(`Batch ${batchNumber} - Full results object:`, results);
            }
            
            // If we got empty results and haven't exceeded retries, wait and retry
            if (retryCount < MAX_RETRIES && (!Array.isArray(results) || results.length === 0)) {
              retryCount++;
              console.warn(`Batch ${batchNumber} - Empty results, retrying (${retryCount}/${MAX_RETRIES})...`);
              await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds before retry
            } else {
              break; // Exit retry loop
            }
          } catch (error) {
            console.error(`Batch ${batchNumber} - Error on attempt ${retryCount + 1}:`, error);
            if (retryCount < MAX_RETRIES) {
              retryCount++;
              await new Promise(resolve => setTimeout(resolve, 2000));
            } else {
              throw error;
            }
          }
        }
        
        if (Array.isArray(results) && results.length > 0) {
          // Merge results into current rows
          currentRows = mergeAITranslationsIntoRows(results, currentRows);
          
          // Capitalize first letters
          currentRows.forEach((row) => {
            validColumns.forEach((lang) => {
              if (row.hasOwnProperty(lang) && row[lang] !== null && row[lang] !== undefined && typeof row[lang] === 'string' && row[lang].length > 0) {
                row[lang] = row[lang].charAt(0).toUpperCase() + row[lang].slice(1);
              }
            });
          });
          
          // Update state immediately after each batch
          setRows(currentRows);
          processedCount += results.length;
          setFillInMissingProgress({ current: processedCount, total: rowsWithMissingTranslations.length });
          
          console.log(`Batch ${batchNumber} completed. Processed ${processedCount}/${rowsWithMissingTranslations.length} cards`);
        } else {
          console.warn(`Batch ${batchNumber} returned empty results after ${retryCount + 1} attempt(s)`);
          console.warn(`Batch ${batchNumber} - Skipping this batch. Cards in batch:`, batch.map(r => {
            const card = {};
            validColumns.forEach(lang => {
              if (r[lang]) card[lang] = r[lang];
            });
            return card;
          }));
        }
        
        // Small delay between batches to avoid rate limiting
        // Increased delay since we're processing one card at a time with many languages
        if (i + BATCH_SIZE < rowsWithMissingTranslations.length) {
          await new Promise(resolve => setTimeout(resolve, 1500));
        }
      }
      
      alert(`Successfully processed ${processedCount} cards with translations.`);
    } catch (error) {
      console.error("Error filling in missing translations:", error);
      alert(`Error: ${error.message}. Processed ${processedCount} cards before error.`);
    } finally {
      setFillInMissingWorking(false);
      setFillInMissingProgress({ current: 0, total: 0 });
    }
  }

  const openProofreaderModal = () => {
    // Filter rows to only include cards with at least one translation
    const validColumns = columns.filter(col => col !== null);
    const cardsWithTranslations = rows.filter(row => {
      return validColumns.some(lang => row[lang] && row[lang] !== null && row[lang] !== "");
    });

    if (cardsWithTranslations.length === 0) {
      alert("No cards with translations found. Please add some translations first.");
      return;
    }

    // Create mapping from filtered index to original row index
    const indexMapping = [];
    rows.forEach((row, originalIndex) => {
      if (validColumns.some(lang => row[lang] && row[lang] !== null && row[lang] !== "")) {
        indexMapping.push(originalIndex);
      }
    });

    setShowProofreaderModal(true);
    // Store mapping in a ref or pass it to modal - for now, we'll use cardId matching
  };

  const exportTable = () => {
    const validColumns = columns.filter(col => col !== null);
    
    // Build header row with language codes
    const headerRow = validColumns.join('\t');
    
    // Build data rows
    const dataRows = rows
      .filter(row => {
        // Only include rows that have at least one value
        return validColumns.some(lang => row[lang] && row[lang] !== null && row[lang] !== "");
      })
      .map(row => {
        return validColumns.map(lang => {
          const value = row[lang] || "";
          // Replace tabs and newlines in values to preserve table structure
          return value.toString().replace(/\t/g, ' ').replace(/\n/g, ' ');
        }).join('\t');
      });
    
    // Combine header and data
    const tableText = [headerRow, ...dataRows].join('\n');
    setExportedTableText(tableText);
    setShowExportModal(true);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(exportedTableText).then(() => {
      alert("Table copied to clipboard!");
    }).catch(err => {
      console.error("Failed to copy:", err);
      alert("Failed to copy to clipboard. Please select and copy manually.");
    });
  };

  const applyProofreaderChanges = async (acceptedSuggestions) => {
    if (!acceptedSuggestions || acceptedSuggestions.length === 0) {
      return;
    }

    // Helper to check for meaningful change (ignoring whitespace)
    const hasMeaningfulChange = (original, suggested) => {
      if (!original || !suggested) return false;
      return original.trim() !== suggested.trim();
    };

    // Filter to only meaningful changes
    const meaningfulSuggestions = acceptedSuggestions.filter(s => 
      hasMeaningfulChange(s.original, s.suggested)
    );

    if (meaningfulSuggestions.length === 0) {
      setNotificationMessage("No meaningful changes to apply. All suggestions only have whitespace differences.");
      setNotificationType("error");
      setShowNotification(true);
      return;
    }

    // Create a map of updates: cardId -> language -> new value
    // Use cardId to find the correct row since cardIndex is relative to filtered array
    const updates = {};
    
    meaningfulSuggestions.forEach(suggestion => {
      // Find the row by cardId (from suggestion) or by matching the filtered card
      const cardId = suggestion.cardId;
      let targetRowIndex = -1;
      
      if (cardId) {
        // Find row by card_id
        targetRowIndex = rows.findIndex(row => row.card_id === cardId);
      }
      
      // If not found by card_id, try to match by cardIndex (filtered array index)
      if (targetRowIndex === -1) {
        const validColumns = columns.filter(col => col !== null);
        const cardsWithTranslations = rows.filter(row => {
          return validColumns.some(lang => row[lang] && row[lang] !== null && row[lang] !== "");
        });
        
        if (suggestion.cardIndex < cardsWithTranslations.length) {
          const filteredCard = cardsWithTranslations[suggestion.cardIndex];
          // Find original index
          targetRowIndex = rows.findIndex(row => {
            if (filteredCard.card_id && row.card_id) {
              return row.card_id === filteredCard.card_id;
            }
            // Match by comparing first translation
            const validColumns = columns.filter(col => col !== null);
            for (const lang of validColumns) {
              if (filteredCard[lang] && row[lang] && filteredCard[lang] === row[lang]) {
                return true;
              }
            }
            return false;
          });
        }
      }
      
      if (targetRowIndex !== -1) {
        const cardId = rows[targetRowIndex].card_id || `temp_${targetRowIndex}`;
        if (!updates[cardId]) {
          updates[cardId] = { cardIndex: targetRowIndex, changes: {} };
        }
        updates[cardId].changes[suggestion.language] = suggestion.suggested;
      }
    });

    // Apply updates to rows state
    const updatedRows = [...rows];
    Object.values(updates).forEach(({ cardIndex, changes }) => {
      if (updatedRows[cardIndex] !== undefined && changes) {
        Object.keys(changes).forEach(language => {
          updatedRows[cardIndex][language] = changes[language];
        });
      }
    });

    setRows(updatedRows);

    // Save to database
    setSaving(true);
    try {
      await saveChangesToDeck({ 
        languages: columns.filter((col) => col !== null), 
        deckId: deck.id 
      });
      await saveCardsToDatabase();
      setNotificationMessage(`Successfully applied ${meaningfulSuggestions.length} suggestion(s).`);
      setNotificationType("success");
      setShowNotification(true);
    } catch (error) {
      console.error("Error applying proofreader changes:", error);
      setNotificationMessage(`Error applying changes: ${error.message}`);
      setNotificationType("error");
      setShowNotification(true);
    } finally {
      setSaving(false);
    }
  };

  const generateMissingAudio = async () => {
    if (!deck || !deck.id) {
      alert("Deck information not available.");
      return;
    }

    const confirmGenerate = window.confirm(
      "This will generate audio for all cards in this deck. This may take a while. Continue?"
    );

    if (!confirmGenerate) {
      return;
    }

    setGeneratingAudio(true);
    try {
      const result = await generateMultilingualDeckAudio({ deckId: deck.id });
      alert(
        result.message + 
        (result.errors && result.errors.length > 0 
          ? `\n\nErrors occurred for ${result.errors.length} card(s).` 
          : "")
      );
    } catch (error) {
      console.error("Error generating audio:", error);
      alert(
        error.message || 
        "An error occurred while generating audio. Please check the console for details."
      );
    } finally {
      setGeneratingAudio(false);
    }
  }


  return (
    <div className="flex flex-col gap-12">

      <div className="flex gap-3 mb-4 justify-center">
        <button
          onClick={addColumn}
          className="px-4 py-2 bg-persian-green text-white rounded-lg shadow-md hover:bg-opacity-90 transition"
        >
          Add language
        </button>
        <button
          onClick={addRow}
          className="px-4 py-2 bg-persian-green text-white rounded-lg shadow-md hover:bg-opacity-90 transition"
        >
          Add card
        </button>
        <button
          onClick={addDefaultLanguages}
          className="px-4 py-2 bg-persian-green text-white rounded-lg shadow-md hover:bg-opacity-90 transition"
        >
          Add default languages
        </button>
        <button
          onClick={addAllTtsLanguages}
          className="px-4 py-2 bg-persian-green text-white rounded-lg shadow-md hover:bg-opacity-90 transition"
        >
          Add All Text-To-Speech Languages
        </button>
        <button onClick={deleteDuplicateRows} className="px-4 py-2 bg-persian-green text-white rounded-lg shadow-md hover:bg-opacity-90 transition">
            Delete duplicates
        </button>
        <button
          onClick={capitalizeClick}
          className="px-4 py-2 bg-persian-green text-white rounded-lg shadow-md hover:bg-opacity-90 transition"
        >
          Capitalize
        </button>
        <button onClick={toggleAIAssistant} className="px-4 py-2 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-lg shadow-md hover:bg-opacity-90 transition">
            {showAIPrompt ? "Close AI assistant" : "Generate with AI"}
        </button>
        <button onClick={fillInMissingTranslationsWithAI} className="px-4 py-2 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-lg shadow-md hover:bg-opacity-90 transition" disabled={fillInMissingWorking}>
            {fillInMissingWorking 
              ? `Processing card ${fillInMissingProgress.current} of ${fillInMissingProgress.total}...` 
              : "Fill in missing translations with AI"}
        </button>
        <button 
          onClick={generateMissingAudio} 
          className="px-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg shadow-md hover:bg-opacity-90 transition"
          disabled={generatingAudio}
        >
          {generatingAudio ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
              Generating audio...
            </>
          ) : (
            "Generate missing audio"
          )}
        </button>
        <button 
          onClick={openProofreaderModal} 
          className="px-4 py-2 bg-gradient-to-r from-green-500 to-teal-500 text-white rounded-lg shadow-md hover:bg-opacity-90 transition"
        >
          AI Proofreader
        </button>
        <button 
          onClick={exportTable} 
          className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-lg shadow-md hover:bg-opacity-90 transition"
        >
          <FontAwesomeIcon icon={faDownload} className="mr-2" />
          Export table
        </button>
        <button 
          onClick={() => setShowAudioRegenerationModal(true)} 
          className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg shadow-md hover:bg-opacity-90 transition"
        >
          <FontAwesomeIcon icon={faVolumeUp} className="mr-2" />
          Check Audio Needs
        </button>

      </div>
      {showAIPrompt && (
  <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-lg flex flex-col place-items-center gap-3">
    <h2 className="text-lg font-semibold text-gray-800 dark:text-white">
      🤖 AI Assistant
    </h2>
        <input
        type="text"
        value={aiPrompt}
        onChange={(e) => setAiPrompt(e.target.value)}
        placeholder="Describe what you need..."
        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none focus:ring-2 focus:ring-persian-green focus:border-transparent transition"
        />
        <button onClick={generateAICardsClick} className="px-4 py-2 bg-gradient-to-r from-purple-500 to-blue-500 text-white font-medium rounded-lg shadow-md hover:bg-opacity-90 transition">
            {aiWorking ? <FontAwesomeIcon icon={faSpinner} spin /> : "Let AI do its thing..."}
        </button>
  </div>
)}


      <div className="overflow-x-auto">
        <table className="w-full border border-gray-300 rounded-lg overflow-hidden shadow-sm min-h-96">
          <thead>
            <tr className="bg-persian-green text-gray-700">
              {columns.map((language, index) => (
                <th key={index} className="border border-gray-300 px-4 py-3 text-left font-semibold relative">
                  <div className="flex items-center gap-2">
                    <Select
                      value={sourceLanguageOptions[1][language]}
                      onChange={(option)=>languageOnChange(option, index)}
                      options={sourceLanguageOptions[0]}
                      autoFocus={false}
                      className="w-48 text-gray-500 dark:text-gray-300 text-base"
                    />
                    {language !== null && (
                      <button
                        onClick={() => deleteLanguage(language, index)}
                        className="text-red-500 hover:text-red-700 cursor-pointer text-sm p-1 transition"
                        title="Delete language"
                        disabled={saving}
                      >
                        <FontAwesomeIcon icon={faX} />
                      </button>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex} className="hover:bg-gray-100 transition relative">
                {columns.map((language, colIndex) => (
                  <td key={colIndex} className="border  border-gray-300 px-4 py-2">
                    <div className="flex flex-row place-items-center gap-2">
                    {colIndex === 0 && 
                    <span className="text-xs text-gray-500">
                      {rowIndex}
                    </span>
                    }
                    <input
                      type="text"
                      value={row[language] || ""}
                      onChange={(e) =>
                        handleInputChange(rowIndex, language, e.target.value)
                      }
                      className="w-full px-2 py-1 border border-gray-300 rounded-md outline-none focus:border-persian-green"
                    />
                    {language !== null && hasAudioForLanguage(row, language) && (
                      <FontAwesomeIcon 
                        icon={faVolumeUp} 
                        className="text-green-500 text-sm flex-shrink-0 cursor-pointer hover:text-green-600 transition-colors" 
                        title={`Click to play audio for ${language}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          const audioUrl = getAudioUrlForLanguage(row, language);
                          if (audioUrl) {
                            playAudio(audioUrl);
                          }
                        }}
                      />
                    )}
                    </div>
                  </td>
                ))}
                <div onClick={()=>deleteRow(row, rowIndex)} className="absolute right-0 top-1 text-red-500 cursor-pointer text-sm ">
                    <FontAwesomeIcon icon={faTrash} />
                </div>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-row justify-center place-items-center gap-8">      
        <Button onClick={saveChangesClick} text="Save changes" color="green" />
        {saving && <FontAwesomeIcon icon={faSpinner} spin />}
      </div>

      {showProofreaderModal && (
        <ProofreaderModal
          closeFunction={() => setShowProofreaderModal(false)}
          cards={rows.filter(row => {
            const validColumns = columns.filter(col => col !== null);
            return validColumns.some(lang => row[lang] && row[lang] !== null && row[lang] !== "");
          })}
          allRows={rows}
          columns={columns}
          deckTitle={deck?.deckName || deck?.name || ""}
          onApplyChanges={applyProofreaderChanges}
        />
      )}

      {showExportModal && (
        <Modal
          content={
            <div className="flex flex-col gap-4">
              <h2 className="text-2xl font-bold dark:text-white">Export Table</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Copy the table data below. The format is tab-separated values (TSV), which can be pasted into Excel, Google Sheets, or other spreadsheet applications.
              </p>
              <div className="flex flex-col gap-2">
                <div className="flex justify-end">
                  <button
                    onClick={copyToClipboard}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition text-sm font-medium flex items-center gap-2"
                  >
                    <FontAwesomeIcon icon={faCopy} />
                    Copy to Clipboard
                  </button>
                </div>
                <textarea
                  value={exportedTableText}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white font-mono text-sm resize-y min-h-[400px]"
                  onClick={(e) => e.target.select()}
                />
              </div>
            </div>
          }
          closeFunction={() => setShowExportModal(false)}
          size="2xl"
        />
      )}

      {showAudioRegenerationModal && (
        <AudioRegenerationModal
          closeFunction={() => setShowAudioRegenerationModal(false)}
          deckId={deck?.id}
        />
      )}

      <NotificationMessage
        showMessageBool={showNotification}
        setShowMessageBool={setShowNotification}
        notificationStatus={notificationType}
        message={notificationMessage}
      />
    </div>
  );
};

export default MultiLingualDeckTable;
